import moment from 'moment-timezone';

/* Convert `attributes` into `pickupFulfillmentTypes` */
export const parseLocationAttributes = (locationAttributes = []) => {
  const isDefaultToCurbsidePickup = !!locationAttributes.find(attr => attr.code === 'DefaultToCurbsidePickup' && attr.value)?.value;

  const isDefaultToDriveThrough = !!locationAttributes.find(attr => attr.code === 'DefaultToDriveThroughPickup' && attr.value)?.value;

  const isDefaultToTakeOut = !isDefaultToCurbsidePickup && !isDefaultToDriveThrough;

  const fulfillmentTypes = [];

  const features = {};

  locationAttributes.forEach(({ code, value }) => {
    /** Set merchant features */
    features[code] = value;

    /** Set merchant pickup fulfillment types */
    if (code === 'Curbside' && value) fulfillmentTypes.push({ displayOrder: 1, value: 'curbside', isDefaultPickupType: isDefaultToCurbsidePickup });

    else if (code === 'OnsitePickup' && value) fulfillmentTypes.push({ displayOrder: 2, value: 'take-out', isDefaultPickupType: isDefaultToTakeOut });

    else if (code === 'DriveThrough' && value) fulfillmentTypes.push({ displayOrder: 3, value: 'drive-through', isDefaultPickupType: isDefaultToDriveThrough });
  });

  return {
    pickupFulfillmentTypes: fulfillmentTypes.sort((a, b) => a.displayOrder - b.displayOrder),
    features
  };
};

export const parseLocationOrderingStatus = (location, currentDayOperatingSchedule) => {
  const {
    allowAfterHoursOrdering,
    allowSameDayOutsideBusinessHoursOrdering,
    outsideGeoFence,
    maximumOrderAheadDays
  } = location;

  const allowImmediateOrdering = currentDayOperatingSchedule?.status === 'Online';

  const allowFutureSameDayOrdering = maximumOrderAheadDays <= 1 && allowSameDayOutsideBusinessHoursOrdering;

  const allowFutureNextDayOrdering = maximumOrderAheadDays > 1 && allowAfterHoursOrdering;

  const isReadyForOrdering = allowImmediateOrdering || allowFutureNextDayOrdering || allowFutureSameDayOrdering;

  let locationOrderingStatus = 'UNAVAILABLE_ORDERING';

  if (allowImmediateOrdering) {
    locationOrderingStatus = 'IMMEDIATE_ORDERING';
  }
  else if (allowFutureSameDayOrdering) {
    locationOrderingStatus = 'FUTURE_SAME_DAY_ORDERING';
  }
  else if (allowFutureNextDayOrdering) {
    locationOrderingStatus = 'FUTURE_NEXT_DAY_ORDERING';
  }

  return outsideGeoFence
    ? { isReadyForOrdering: false, locationOrderingStatus: 'UNAVAILABLE_ORDERING' }
    : { isReadyForOrdering, locationOrderingStatus };
};


function parseLocation(options) {
  const { location, merchant, orderDetails = {} } = options;

  const { features, pickupFulfillmentTypes } = parseLocationAttributes(location.attributes);

  const computed = {
    address: location.address || {},
    allowNoPaymentInfo: false,
    attributes: location.attributes || [],
    currentDayOperatingSchedule: null,
    defaultTabAmount: null,
    description: location.displayName || location.description,
    features,
    hasStoreHours: false,
    isCurrentServiceAvailable: true,
    isReadyForOrdering: true,
    locationOrderingStatus: 'IMMEDIATE_ORDERING',
    leadPickupTime: location.leadPickupTime || '00:15:00',
    nextOpening: null,
    paymentGateway: location.paymentGateway || { methods: {}, providers: {} },
    pickupFulfillmentTypes,
    tippingOptions: location.tippingOptions || [],
    validations: location.validations || [],
    isOpenTabEnabled: false
  };

  const merchantFeatures = {};
  if (merchant && merchant.appSettings && merchant.appSettings.resources) {
    merchant.appSettings.resources.forEach((resource) => {
      const hasBooleanStringValue = resource.value && ['true', 'false'].some(val => resource.value === val);
      merchantFeatures[resource.key] = hasBooleanStringValue ? resource.value === 'true' : resource.value;
    });
  }

  /**
  * if there is a selected location, set these values
  * to false so that they can be flipped back to true
  * based on the location's configuration
  */
  if (options.location.storeId) {
    computed.isReadyForOrdering = false;
    computed.isCurrentServiceAvailable = false;
  }

  /**
  * if the primary merchant ordering service is disabled for the
  * current order type, then the current ordering service is not available
  */
  const hasUnavailableOrderTypeService = [
    (orderDetails.orderType === 'table' && !merchantFeatures['order-at-table']),
    (orderDetails.orderType === 'pay' && !merchantFeatures['pay-at-table']),
    (['standard', 'catering', 'group'].includes(orderDetails.orderType) && !merchantFeatures['order-ahead'])
  ].some(Boolean);

  if (hasUnavailableOrderTypeService) {
    computed.isCurrentServiceAvailable = false;
  }

  /* Daily operating schedules */
  if (location.dailyOperatingSchedules) {
    const dayNames = Object.keys(location.dailyOperatingSchedules);
    const now = moment.tz(location.timeZone);
    const currentDayName = now.format('dddd').toLowerCase();
    const currentDayNameIndex = dayNames.indexOf(currentDayName);
    const dayNamesToWrap = dayNames.splice(0, currentDayNameIndex);
    const dayNamesOrdered = [...dayNames, ...dayNamesToWrap];
    const nextSchedules = [];

    dayNamesOrdered.forEach((dayName, i) => {
      const schedule = location.dailyOperatingSchedules[dayName];

      if (schedule.isCurrentDay) {
        computed.currentDayOperatingSchedule = schedule;
      }

      /* NOTE: Checking for `open` ensures this day has some type of hours configured */
      if (schedule.open) {
        computed.hasStoreHours = true;

        /* NOTE: Checking for `isOpen` simply ensures the store will be open at some point for the day */
        if (schedule.isOpen) {
          schedule.openCloseItems.forEach((item) => {
            const openSegments = item.open.split(':');
            const time = moment.tz(location.timeZone).add(i, 'days');

            nextSchedules.push({
              close: item.close,
              openTime: time.hours(openSegments[0]).minutes(openSegments[1]).seconds(0),
              schedule
            });
          });
        }
      }
    });

    const nextOpeningSchedule = nextSchedules.find(schedule => schedule.openTime.diff(now) > 0);

    if (nextOpeningSchedule) {
      computed.nextOpening = {
        openTimestamp: nextOpeningSchedule.openTime.utc().format(),
        closeTime: nextOpeningSchedule.close
      };
    }
  }

  /* Determine ability to order */

  /**
   * Determine if web ordering is enabled for the selected location
   * (as opposed to "mobile ordering" via the native mobile apps)
   */
  const locationWebOrderingAttr = computed.attributes.find(attribute => attribute.code === 'WebOrdering');
  const isWebOrderingEnabled = !!locationWebOrderingAttr?.value;

  if (isWebOrderingEnabled) {
    /**
      * Not all locations will have an `order-ahead` attribute.
      * The lack of this attribute does not mean it's disabled,
      * only that we should fall back to the merchant's `order-ahead` value.
      */
    const locationOrderAheadAttr = computed.attributes.find(attribute => attribute.code === 'order-ahead');
    const isOrderAheadEnabled = ['standard', 'catering', 'group'].includes(orderDetails.orderType) && (!locationOrderAheadAttr || locationOrderAheadAttr.value) && merchantFeatures['order-ahead'];
    const isOrderAtTableEnabled = orderDetails.orderType === 'table' && merchantFeatures['order-at-table'];
    const isPayAtTableEnabled = orderDetails.orderType === 'pay' && merchantFeatures['pay-at-table'];

    /**
     * Check if merchant/selected location supports ordering service for the current order type
     */
    if ((isOrderAheadEnabled || isOrderAtTableEnabled || isPayAtTableEnabled)) {
      computed.isCurrentServiceAvailable = true;
    }
  }
  /**
   * Validate a locations ordering status only if a location has been selected, else default to computed values.
  */
  if (location.storeId) {
    const { isReadyForOrdering, locationOrderingStatus } = parseLocationOrderingStatus(location, computed.currentDayOperatingSchedule);
    computed.isReadyForOrdering = isReadyForOrdering;
    computed.locationOrderingStatus = locationOrderingStatus;
  }

  /* Payment Gateways */
  const providers = Object.values(computed.paymentGateway.providers);

  const { defaultTabAmount, supportsOpenTab } = providers.find(p => p.supportsOpenTab) || {};
  if (defaultTabAmount) {
    computed.defaultTabAmount = defaultTabAmount;
    computed.isOpenTabEnabled = supportsOpenTab;
  }


  const supportsNoPayment = providers.some(provider => provider.allowDeferredPayment);
  if (supportsNoPayment) computed.allowNoPaymentInfo = true;

  return {
    ...location,
    ...computed
  };
}

export default parseLocation;
